import { useState, useEffect } from "react";
import { Btn, Modal, Field, Inp, Sel, Txta, FormRow, Stage, KpiStrip, ExpandTable, Selo, Chips, Detalhe } from "../components/primitives";

// ── Integração n8n (via /api/n8n — proxy serverless no Vercel) ──
async function fetchN8nStatus() {
  const res = await fetch("/api/n8n");
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}
async function toggleN8nWorkflow(nome, active) {
  const res = await fetch("/api/n8n", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ nome, active }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

const STATUS_TONE = { "Ativo": "ok", "Com problema": "bad", "Inativo": "off", "Não testado": "warn" };
const STATUSES = ["Ativo", "Com problema", "Não testado", "Inativo"];
const CANAIS   = ["WhatsApp", "Email", "Supabase", "N8N", "Evolution API", "Outro"];

function fmtDisparo(ts) {
  if (!ts) return null;
  const d   = new Date(ts);
  const now = new Date();
  const diff = Math.floor((now - d) / 60000); // minutos
  if (diff < 60)   return `${diff}min atrás`;
  if (diff < 1440) return `${Math.floor(diff / 60)}h atrás`;
  const dias = Math.floor(diff / 1440);
  return `${dias} dia${dias > 1 ? "s" : ""} atrás`;
}

function AutomacaoModal({ initial, onClose, onSave }) {
  const [f, setF] = useState(initial || {
    nome: "", descricao: "", quando: "", canal: "WhatsApp", status: "Não testado", notas: "",
  });
  const up = (k, v) => setF(p => ({ ...p, [k]: v }));
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!f.nome.trim()) return;
    setSaving(true);
    await onSave(f);
    setSaving(false);
  }

  return (
    <Modal title={initial ? "Editar automação" : "Nova automação"} onClose={onClose} width={520}>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <Field label="Nome da automação">
          <Inp value={f.nome} onChange={e => up("nome", e.target.value)} placeholder="Ex: Relatório de sábado" />
        </Field>
        <Field label="Descrição">
          <Txta rows={3} value={f.descricao || ""} onChange={e => up("descricao", e.target.value)}
            placeholder="O que essa automação faz, de onde lê os dados, para onde envia..." />
        </Field>
        <FormRow>
          <Field label="Quando dispara" half>
            <Inp value={f.quando || ""} onChange={e => up("quando", e.target.value)} placeholder="Ex: Sábado às 8h" />
          </Field>
          <Field label="Canal" half>
            <Sel value={f.canal || "WhatsApp"} onChange={e => up("canal", e.target.value)}>
              {CANAIS.map(c => <option key={c}>{c}</option>)}
            </Sel>
          </Field>
        </FormRow>
        <Field label="Status">
          <Sel value={f.status} onChange={e => up("status", e.target.value)}>
            {STATUSES.map(s => <option key={s}>{s}</option>)}
          </Sel>
        </Field>
        <Field label="Observações">
          <Txta rows={2} value={f.notas || ""} onChange={e => up("notas", e.target.value)}
            placeholder="Problemas encontrados, últimos testes, dependências..." />
        </Field>
        <Btn variant="accent" full onClick={save} disabled={saving || !f.nome.trim()}>
          {saving ? "Salvando..." : "Salvar"}
        </Btn>
      </div>
    </Modal>
  );
}

export function AutomacoesView({ automacoes = [], onUpdate, onNew, onDelete }) {
  const [editModal, setEditModal]   = useState(null);
  const [showNew,   setShowNew]     = useState(false);
  const [localNotes, setLocalNotes]         = useState({});
  const [localTemplate, setLocalTemplate]   = useState({});
  const [showTemplate, setShowTemplate]     = useState({});
  const [savedTemplate, setSavedTemplate]   = useState({});

  // ── Estado real dos workflows no n8n ──
  const [n8nStatus, setN8nStatus]   = useState(null);   // { [nome]: { active, lastExecution } }
  const [n8nErro, setN8nErro]       = useState(false);
  const [toggling, setToggling]     = useState({});      // { [nome]: true } enquanto liga/desliga

  useEffect(() => {
    fetchN8nStatus()
      .then((d) => { setN8nStatus(d); setN8nErro(false); })
      .catch(() => setN8nErro(true));
  }, []);

  async function handleToggle(nome, novoAtivo) {
    setToggling((p) => ({ ...p, [nome]: true }));
    try {
      const r = await toggleN8nWorkflow(nome, novoAtivo);
      setN8nStatus((p) => ({ ...p, [nome]: { ...(p?.[nome] || {}), active: r.active } }));
      // sincroniza o status descritivo da tabela rc2_automacoes
      const auto = automacoes.find((a) => a.nome === nome);
      if (auto) await onUpdate(auto.id, { status: r.active ? "Ativo" : "Inativo" });
    } catch (e) {
      alert(`Falha ao ${novoAtivo ? "ligar" : "desligar"} no n8n: ${e.message || e}`);
    } finally {
      setToggling((p) => ({ ...p, [nome]: false }));
    }
  }

  function fmtExec(le) {
    if (!le) return null;
    const ok   = le.status === "success";
    const when = fmtDisparo(le.startedAt);
    return { ok, texto: `${ok ? "✓ sucesso" : "✗ erro"}${when ? ` · ${when}` : ""}` };
  }

  const sorted = [...automacoes].sort((a, b) => (a.ordem || 0) - (b.ordem || 0));

  const counts = {
    ativo:       automacoes.filter(a => a.status === "Ativo").length,
    problema:    automacoes.filter(a => a.status === "Com problema").length,
    naoTestado:  automacoes.filter(a => a.status === "Não testado").length,
    inativo:     automacoes.filter(a => a.status === "Inativo").length,
  };

  return (
    <div className="view" style={{ maxWidth: 980 }}>

      {/* ── Palco ────────────────────────────────────────────── */}
      <Stage
        path="automacoes"
        title="Automações"
        hl="no ar"
        lead="N8N · Evolution API · Supabase"
        actions={<Btn sm variant="primary" onClick={() => setShowNew(true)}>+ Nova automação</Btn>}
      />

      {/* ── Aviso quando o n8n não responde ─────────────────── */}
      {n8nErro && (
        <div className="alert-line warn">
          Não foi possível consultar o n8n agora: os interruptores de ligar/desligar ficam ocultos. Abaixo, o registro manual do CRM.
        </div>
      )}

      {/* ── Resumo ───────────────────────────────────────────── */}
      {automacoes.length > 0 && (
        <KpiStrip style={{ marginBottom: 16 }} items={[
          { label: "Ativas", value: counts.ativo },
          { label: "Com problema", value: counts.problema, sub: counts.problema ? "agir" : "nenhuma", tone: counts.problema ? "bad" : "ok" },
          { label: "Não testadas", value: counts.naoTestado, sub: counts.naoTestado ? "testar" : undefined, tone: "warn" },
          { label: "Inativas", value: counts.inativo },
        ]} />
      )}

      {/* ── Lista: uma linha por automação ───────────────────── */}
      <ExpandTable
        rows={sorted}
        minWidth={620}
        empty="Nenhuma automação cadastrada. Adicione as automações do n8n para acompanhar o status."
        columns={[
          { key: "nome", label: "Automação", render: (auto) => (
            <span><span className="name">{auto.nome}</span>{auto.quando && <span className="sub"> · {auto.quando}</span>}</span>
          ) },
          { key: "status", label: "Situação", render: (auto) => <Selo tone={STATUS_TONE[auto.status] || "warn"}>{auto.status || "Não testado"}</Selo> },
          { key: "n8n", label: "n8n", render: (auto) => {
            const n8n = n8nStatus ? n8nStatus[auto.nome] : null;
            if (!n8n || n8n.error) return <span className="sub">—</span>;
            return <Selo tone={n8n.active ? "ok" : "off"} dot={false}>{n8n.active ? "ligada" : "desligada"}</Selo>;
          } },
          { key: "disparo", label: "Último disparo", align: "right", render: (auto) => <span className="sub">{fmtDisparo(auto.ultimo_disparo) || "—"}</span> },
        ]}
        detail={(auto) => {
          const noteVal = localNotes[auto.id] !== undefined ? localNotes[auto.id] : (auto.notas || "");
          const n8n     = n8nStatus ? n8nStatus[auto.nome] : null;
          const exec    = n8n && !n8n.error ? fmtExec(n8n.lastExecution) : null;
          const busy    = !!toggling[auto.nome];
          return (
            <div className="detail-grid">
              <Detalhe label="O que faz">{auto.descricao}</Detalhe>
              <Detalhe label="Canal">{auto.canal}</Detalhe>

              {/* Controle real no n8n */}
              {n8n && !n8n.error && (
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <button
                    type="button"
                    onClick={() => !busy && handleToggle(auto.nome, !n8n.active)}
                    disabled={busy}
                    aria-pressed={!!n8n.active}
                    title={n8n.active ? "Desligar no n8n" : "Ligar no n8n"}
                    style={{
                      width: 40, height: 22, borderRadius: 999, border: "none",
                      cursor: busy ? "wait" : "pointer", position: "relative", flexShrink: 0,
                      background: n8n.active ? "var(--success)" : "var(--line-strong)",
                      transition: "background .2s", opacity: busy ? 0.6 : 1,
                    }}
                  >
                    <span style={{
                      position: "absolute", top: 3, left: n8n.active ? 21 : 3,
                      width: 16, height: 16, borderRadius: "50%", background: "var(--surface)",
                      transition: "left .2s", boxShadow: "0 1px 2px rgba(0,0,0,.2)",
                    }} />
                  </button>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>
                    {busy ? "Aguarde..." : n8n.active ? "Ligada no n8n" : "Desligada no n8n"}
                  </span>
                  {exec && <Selo tone={exec.ok ? "ok" : "bad"} dot={false}>última execução: {exec.texto}</Selo>}
                </div>
              )}

              <div className="detalhe">
                <span className="eyebrow">Situação no CRM</span>
                <Chips
                  label="Situação"
                  items={STATUSES.map((st) => ({ id: st, label: st }))}
                  active={auto.status}
                  onChange={(st) => onUpdate(auto.id, { status: st })}
                />
              </div>

              {/* Template de mensagem */}
              {auto.template_mensagem !== undefined && auto.canal !== "Supabase" && (
                <div className="detalhe">
                  <button type="button" className="btn btn-ghost btn-sm" style={{ justifySelf: "start" }}
                    onClick={() => setShowTemplate((prev) => ({ ...prev, [auto.id]: !prev[auto.id] }))}>
                    {showTemplate[auto.id] ? "▴" : "▾"} Template da mensagem
                  </button>
                  {showTemplate[auto.id] && (
                    <div style={{ display: "grid", gap: 8 }}>
                      <span className="sub" style={{ color: "var(--faint)", fontSize: 12 }}>
                        Variáveis: {"{secretaria}"}, {"{cliente}"}, {"{lista_clientes}"}, {"{data}"}
                      </span>
                      <Txta
                        rows={5}
                        value={localTemplate[auto.id] !== undefined ? localTemplate[auto.id] : (auto.template_mensagem || "")}
                        onChange={(e) => {
                          const v = e.target.value;
                          setLocalTemplate((prev) => ({ ...prev, [auto.id]: v }));
                          setSavedTemplate((prev) => ({ ...prev, [auto.id]: false }));
                        }}
                        placeholder="Texto da mensagem WhatsApp. Use {secretaria}, {cliente}, {lista_clientes}, {data} como variáveis."
                      />
                      <div className="detail-actions">
                        <Btn sm variant="primary" onClick={async () => {
                          const val = localTemplate[auto.id] !== undefined ? localTemplate[auto.id] : (auto.template_mensagem || "");
                          await onUpdate(auto.id, { template_mensagem: val });
                          setSavedTemplate((prev) => ({ ...prev, [auto.id]: true }));
                          setTimeout(() => setSavedTemplate((prev) => ({ ...prev, [auto.id]: false })), 2500);
                        }}>Salvar template</Btn>
                        {savedTemplate[auto.id] && <Selo tone="ok" dot={false}>✓ Salvo</Selo>}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Notas (salva ao sair do campo) */}
              <div className="detalhe">
                <span className="eyebrow">Observações</span>
                <Txta
                  rows={2}
                  value={noteVal}
                  onChange={(e) => { const v = e.target.value; setLocalNotes((prev) => ({ ...prev, [auto.id]: v })); }}
                  onBlur={async (e) => {
                    if (e.target.value !== (auto.notas || "")) {
                      await onUpdate(auto.id, { notas: e.target.value });
                      setLocalNotes((prev) => { const n = { ...prev }; delete n[auto.id]; return n; });
                    }
                  }}
                  placeholder="Problemas identificados, última verificação manual, dependências..."
                />
              </div>

              <div className="detail-actions">
                <Btn sm variant="secondary" onClick={() => setEditModal(auto)}>Editar</Btn>
              </div>
            </div>
          );
        }}
      />

      {/* ── Modais ───────────────────────────────────────────── */}
      {showNew && (
        <AutomacaoModal
          onClose={() => setShowNew(false)}
          onSave={async data => { await onNew(data); setShowNew(false); }}
        />
      )}
      {editModal && (
        <AutomacaoModal
          initial={editModal}
          onClose={() => setEditModal(null)}
          onSave={async data => {
            await onUpdate(editModal.id, data);
            setEditModal(null);
          }}
        />
      )}
    </div>
  );
}
